import { useMemo, useEffect } from 'react'
import { useEditableConfig } from './hooks/useEditableConfig'
import Hero from './components/Hero'
import Works from './components/Works'
import Collaboration from './components/Collaboration'
import About from './components/About'
import Contact from './components/Contact'
import Footer from './components/Footer'
import EditButton from './components/EditButton'
import EditPanel from './components/EditPanel'
import { isLightColor } from './utils/colors'

// 字体映射
const fontMap = {
  inter: "'Inter', 'Noto Sans SC', system-ui, sans-serif",
  'noto-sans': "'Noto Sans SC', system-ui, sans-serif",
  'noto-serif': "'Noto Serif SC', 'SimSun', serif",
  zcool: "'ZCOOL KuaiLe', 'Comic Sans MS', cursive",
  xiaowei: "'ZCOOL XiaoWei', 'SimSun', serif",
  mashan: "'Ma Shan Zheng', 'KaiTi', cursive",
  longcang: "'Long Cang', 'KaiTi', cursive",
  system: "system-ui, -apple-system, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif",
}

// 将 hex 颜色转为 RGB 字符串
function hexToRgb(hex) {
  const h = hex.replace('#', '')
  const bigint = parseInt(h, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `${r}, ${g}, ${b}`
}

// 计算浅色强调色（用于渐变）
function lighten(hex, amt = 32) {
  const h = hex.replace('#', '')
  let r = parseInt(h.substring(0, 2), 16)
  let g = parseInt(h.substring(2, 4), 16)
  let b = parseInt(h.substring(4, 6), 16)
  r = Math.min(255, r + amt)
  g = Math.min(255, g + amt)
  b = Math.min(255, b + amt)
  return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`
}

// 计算深色强调色
function darken(hex, amt = 20) {
  const h = hex.replace('#', '')
  let r = parseInt(h.substring(0, 2), 16)
  let g = parseInt(h.substring(2, 4), 16)
  let b = parseInt(h.substring(4, 6), 16)
  r = Math.max(0, r - amt)
  g = Math.max(0, g - amt)
  b = Math.max(0, b - amt)
  return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`
}

function App() {
  const {
    config,
    isEditing,
    setIsEditing,
    updateProfile,
    updateStats,
    updatePlatforms,
    updatePlatform,
    updateWorks,
    addWork,
    deleteWork,
    updateCollaboration,
    updateSocial,
    updateContact,
    resetToDefault,
    exportConfig,
    importConfig
  } = useEditableConfig()

  const actions = {
    updateProfile,
    updateStats,
    updatePlatforms,
    updatePlatform,
    updateWorks,
    addWork,
    deleteWork,
    updateCollaboration,
    updateSocial,
    updateContact,
    resetToDefault,
    exportConfig,
    importConfig
  }

  // 检测板块背景是否为浅色 → 自动切换文字颜色
  const isLightBg = useMemo(
    () => isLightColor(config.profile?.sectionBackground || '#000000'),
    [config.profile?.sectionBackground]
  )

  // 同步强调色 + 字体颜色到 CSS 变量
  useEffect(() => {
    const accent = config.profile?.accentColor || '#8b5cf6'
    const fontColor = config.profile?.fontColor || '#ffffff'

    // 设置强调色 CSS 变量
    document.documentElement.style.setProperty('--accent', accent)
    document.documentElement.style.setProperty('--accent-rgb', hexToRgb(accent))
    document.documentElement.style.setProperty('--accent-light', lighten(accent, 32))
    document.documentElement.style.setProperty('--accent-dark', darken(accent, 20))

    // 设置字体颜色 CSS 变量
    document.documentElement.style.setProperty('--font-color', fontColor)
    document.documentElement.style.setProperty('--font-color-rgb', hexToRgb(fontColor))
  }, [config.profile?.accentColor, config.profile?.fontColor])

  return (
    <div className="min-h-screen" style={{
      backgroundColor: config.profile?.sectionBackground || '#000000',
      fontFamily: fontMap[config.profile?.fontFamily] || fontMap.inter,
      color: config.profile?.fontColor || '#ffffff'
    }}>
      <Hero config={config} />
      <Works config={config} isLight={isLightBg} />
      <Collaboration config={config} isLight={isLightBg} />
      <About config={config} isLight={isLightBg} />
      <Contact config={config} isLight={isLightBg} />
      <Footer config={config} isLight={isLightBg} />

      <EditButton onClick={() => setIsEditing(true)} />
      <EditPanel
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        config={config}
        actions={actions}
      />
    </div>
  )
}

export default App
